import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { updateUserRoles, resetUserPassword, type SiteRoleAssignmentInput } from '../../api/users';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import type { AppUser, CustomerSite } from '../../types/api';

interface EditUserModalProps {
  orgId: string;
  sites: CustomerSite[];
  open: boolean;
  onClose: () => void;
  user: AppUser | null;
}

interface RoleRow {
  key: number;
  siteId: string;
  role: 'SIGNING_AUTHORITY' | 'END_USER';
}

type OrgRole = 'NONE' | 'CUSTOMER_ADMIN' | 'REPORTS_VIEWER';

let rowKey = 0;

// Edits an existing user's roles at this org (site roles + Customer Admin)
// and, optionally in the same form, sets them a new password - the two
// things Perry asked for together ("edit user roles and change their
// passwords"). Mirrors NewUserModal's role-row editor so it's a familiar
// pattern, just pre-filled from the user's current roles at this org.
export function EditUserModal({ orgId, sites, open, onClose, user }: EditUserModalProps) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [orgRole, setOrgRole] = useState<OrgRole>('NONE');
  const [roleRows, setRoleRows] = useState<RoleRow[]>([]);
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !user) return;
    const isCustomerAdmin = user.orgRoles.some(
      (r) => r.customerOrganisationId === orgId && r.role === 'CUSTOMER_ADMIN',
    );
    const isReportsViewer = user.orgRoles.some(
      (r) => r.customerOrganisationId === orgId && r.role === 'REPORTS_VIEWER',
    );
    setOrgRole(isCustomerAdmin ? 'CUSTOMER_ADMIN' : isReportsViewer ? 'REPORTS_VIEWER' : 'NONE');
    setRoleRows(
      user.siteRoles
        .filter((r) => r.site?.customerOrganisationId === orgId)
        .map((r) => ({ key: rowKey++, siteId: r.siteId, role: r.role as 'SIGNING_AUTHORITY' | 'END_USER' })),
    );
    setNewPassword('');
    setError(null);
  }, [open, user, orgId]);

  function addRoleRow() {
    setRoleRows((prev) => [...prev, { key: rowKey++, siteId: sites[0]?.id ?? '', role: 'END_USER' }]);
  }

  function updateRoleRow(key: number, patch: Partial<RoleRow>) {
    setRoleRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function removeRoleRow(key: number) {
    setRoleRows((prev) => prev.filter((r) => r.key !== key));
  }

  const rolesMutation = useMutation({
    mutationFn: () => {
      const siteRoles: SiteRoleAssignmentInput[] = roleRows
        .filter((r) => r.siteId)
        .map((r) => ({ siteId: r.siteId, role: r.role }));
      return updateUserRoles(user!.id, {
        orgId,
        isCustomerAdmin: orgRole === 'CUSTOMER_ADMIN',
        isReportsViewer: orgRole === 'REPORTS_VIEWER',
        siteRoles,
      });
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not update this user\'s roles.')),
  });

  const passwordMutation = useMutation({
    mutationFn: () => resetUserPassword(user!.id, newPassword),
    onError: (err) => setError(apiErrorMessage(err, 'Could not set the new password.')),
  });

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword && newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }

    try {
      await rolesMutation.mutateAsync();
      if (newPassword) {
        await passwordMutation.mutateAsync();
      }
      queryClient.invalidateQueries({ queryKey: ['users', orgId] });
      showToast(newPassword ? `${user!.email} updated, password reset.` : `${user!.email} updated.`);
      onClose();
    } catch {
      // error state already set by whichever mutation's onError fired
    }
  }

  const isSaving = rolesMutation.isPending || passwordMutation.isPending;

  if (!user) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit ${user.email}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button form="edit-user-form" type="submit" isLoading={isSaving}>
            Save
          </Button>
        </>
      }
    >
      <form id="edit-user-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}

        <Select
          label="Organisation role"
          value={orgRole}
          onChange={(e) => setOrgRole(e.target.value as OrgRole)}
          hint="Customer Admin can manage sites, users, catalog and settings. Reports Viewer can only see the Reports tab."
        >
          <option value="NONE">None</option>
          <option value="CUSTOMER_ADMIN">Customer Admin</option>
          <option value="REPORTS_VIEWER">Reports Viewer</option>
        </Select>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">Site roles</span>
            <Button type="button" variant="ghost" size="sm" onClick={addRoleRow} disabled={sites.length === 0}>
              <Plus className="h-3.5 w-3.5" /> Add site role
            </Button>
          </div>
          {sites.length === 0 ? (
            <p className="text-xs text-slate-500">This org has no sites yet.</p>
          ) : roleRows.length === 0 && orgRole === 'NONE' ? (
            <p className="text-xs text-amber-600">
              Without a site role or an organisation role, this user won't be able to access anything - and won't
              show up in this list.
            </p>
          ) : null}
          <div className="flex flex-col gap-2">
            {roleRows.map((row) => (
              <div key={row.key} className="flex items-center gap-2">
                <Select
                  className="flex-1"
                  value={row.siteId}
                  onChange={(e) => updateRoleRow(row.key, { siteId: e.target.value })}
                >
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
                <Select
                  className="w-44"
                  value={row.role}
                  onChange={(e) => updateRoleRow(row.key, { role: e.target.value as RoleRow['role'] })}
                >
                  <option value="END_USER">End User</option>
                  <option value="SIGNING_AUTHORITY">Signing Authority</option>
                </Select>
                <button
                  type="button"
                  onClick={() => removeRoleRow(row.key)}
                  className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <Input
          label="Set a new password (optional)"
          type="password"
          minLength={8}
          hint="Leave blank to keep their current password. At least 8 characters if set."
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
      </form>
    </Modal>
  );
}
